import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';
import { cn } from '@/lib/utils';
import {
  buildWhatsAppMessage,
  openWhatsAppChat,
  isValidWhatsAppPhone,
  type WhatsAppMessageType,
} from '@/lib/utils/whatsapp';
import { MessageCircle } from 'lucide-react';

/** Shared green WhatsApp styling for all instances */
export const whatsAppButtonClassName =
  'border-green-400 bg-green-50 text-green-800 shadow-sm hover:!bg-green-100 hover:!border-green-500 hover:!text-green-900 focus-visible:ring-green-400';

interface WhatsAppShareButtonProps {
  phoneNumber?: string | null;
  patientName: string;
  patientId?: string;
  reason?: string;
  doctorName?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  showLabel?: boolean;
  /** Single template message — no dropdown */
  messageType?: WhatsAppMessageType;
  /** Full custom message (e.g. prescription summary) — no dropdown */
  customMessage?: string;
}

const MESSAGE_OPTIONS: { type: WhatsAppMessageType; label: string }[] = [
  { type: 'visit_registered', label: 'Welcome — visit registered' },
  { type: 'queue_update', label: 'You are in the queue' },
  { type: 'your_turn', label: "It's your turn" },
  { type: 'prescription', label: 'Prescription ready' },
  { type: 'follow_up', label: 'Follow-up reminder' },
];

function buttonClassName(showLabel: boolean, className?: string) {
  return cn(
    whatsAppButtonClassName,
    !showLabel && 'h-8 w-8 p-0 shrink-0',
    className
  );
}

export function WhatsAppShareButton({
  phoneNumber,
  patientName,
  patientId,
  reason,
  doctorName,
  variant = 'outline',
  size = 'default',
  className = '',
  showLabel = true,
  messageType,
  customMessage,
}: WhatsAppShareButtonProps) {
  const { toast } = useToast();
  const hospitalName = useHospitalProfileStore((s) => s.profile.name);

  const valid = isValidWhatsAppPhone(phoneNumber);
  const resolvedSize = !showLabel ? 'icon' : size;
  const btnClass = buttonClassName(showLabel, className);

  const sendMessage = (message: string) => {
    const ok = openWhatsAppChat(phoneNumber, message);
    if (!ok) {
      toast({
        title: 'Invalid phone number',
        description: 'Use a valid mobile number (e.g. 10-digit Indian number).',
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: 'Opening WhatsApp',
      description: 'Check the message, then tap Send in WhatsApp.',
    });
  };

  const send = (type: WhatsAppMessageType) => {
    sendMessage(
      buildWhatsAppMessage(type, {
        patientName,
        hospitalName,
        patientId,
        reason,
        doctorName,
      })
    );
  };

  const iconClass = showLabel ? 'h-4 w-4 mr-2' : 'h-4 w-4';

  if (!valid) {
    const missing = phoneNumber == null || String(phoneNumber).trim() === '';
    return (
      <Button
        variant={variant}
        size={resolvedSize}
        className={cn(btnClass, 'opacity-50 cursor-not-allowed')}
        disabled
        title={
          missing
            ? 'No contact number saved for this patient'
            : 'Invalid contact number'
        }
      >
        <MessageCircle className={iconClass} />
        {showLabel && <span>WhatsApp</span>}
      </Button>
    );
  }

  if (customMessage || messageType) {
    return (
      <Button
        type="button"
        variant={variant}
        size={resolvedSize}
        className={btnClass}
        onClick={() =>
          customMessage ? sendMessage(customMessage) : send(messageType!)
        }
      >
        <MessageCircle className={iconClass} />
        {showLabel && <span>WhatsApp</span>}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={variant} size={resolvedSize} className={btnClass}>
          <MessageCircle className={iconClass} />
          {showLabel && <span>WhatsApp</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Send via WhatsApp</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {MESSAGE_OPTIONS.map((opt) => (
          <DropdownMenuItem key={opt.type} onClick={() => send(opt.type)}>
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
